import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatMoney,
  fromCents,
  LEDGER_CYCLE_LABELS,
  LEDGER_CYCLES,
  type LedgerAccountResponse,
  ledgerBalance,
  type LedgerCycle,
  type LedgerEntryResponse,
  type LedgerImportResult,
  type LedgerResponse,
  type LedgerYear,
  toCents,
} from '@k-management/shared';
import { type ChangeEvent, Fragment, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { Icon } from '../../components/Icon.js';
import { amountSort, SortHeader, useTableSort } from '../../components/SortHeader.js';
import { type Command, useCommands, usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate, formatTimestamp } from '../../lib/format.js';
import { useSubjectSearch } from '../quotes/quotes-ui.js';
import { useSessionValue } from '../../lib/preferences.js';
import { LedgerMatchesView } from './LedgerMatchesView.js';
import { LedgerSquaringView } from './LedgerSquaringView.js';

const keys = {
  years: ['treasury', 'ledgers'] as const,
  year: (year: number) => ['treasury', 'ledgers', year] as const,
  entries: (id: string) => ['treasury', 'ledgers', 'entries', id] as const,
};

type MatchFilter = 'all' | 'matched' | 'unmatched';
type View = 'soggetti' | 'abbinamenti' | 'quadratura';

/**
 * Tesoreria › Mastrini (07/10/2026): the customer and supplier ledgers of the
 * accountant, one year at a time, in the direction of each cycle (a
 * customer's credit, a supplier's debt), with their lines and the match to
 * Anagrafica. Read-only: a year is replaced by loading it again.
 */
export function LedgersPage() {
  usePageTitle('Mastrini');
  const canWrite = useCan('tesoreria.write');
  const canCreate = useCan('anagrafica.write');
  const years = useQuery({
    queryKey: keys.years,
    queryFn: () => apiRequest<LedgerYear[]>('/treasury/ledgers'),
  });
  const [year, setYear] = useSessionValue<number | null>('list.ledgers.year', null);
  const shown = year ?? years.data?.[0]?.year ?? null;
  const [cycle, setCycle] = useSessionValue<LedgerCycle>('list.ledgers.cycle', 'attivo');
  const [query, setQuery] = useSessionValue('list.ledgers.query', '');
  const [onlyOpen, setOnlyOpen] = useSessionValue('list.ledgers.onlyOpen', true);
  const [match, setMatch] = useSessionValue<MatchFilter>('list.ledgers.match', 'all');
  const [view, setView] = useSessionValue<View>('list.ledgers.view', 'soggetti');
  // The matches of a subject, from the squaring (read by the matches when they open).
  const [, setMatchesQuery] = useSessionValue('list.ledger-matches.query', '');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState<LedgerAccountResponse | null>(null);
  const ledger = useQuery({
    queryKey: keys.year(shown ?? 0),
    queryFn: () => apiRequest<LedgerResponse>(`/treasury/ledgers/${shown}`),
    enabled: shown !== null,
  });

  useCommands(
    canWrite
      ? [
          {
            key: 'load',
            label: 'Carica il mastrino dell’anno',
            icon: 'upload',
            primary: true,
            onClick: () => setLoading(true),
          } satisfies Command,
        ]
      : [],
  );

  const ofCycle = (ledger.data?.accounts ?? []).filter((account) => account.cycle === cycle);
  const needle = query.trim().toLowerCase();
  const rows = ofCycle.filter(
    (account) =>
      (!onlyOpen || toCents(account.closing) !== 0) &&
      (match === 'all' ||
        (match === 'matched'
          ? account.subject !== null
          : account.partitario && !account.subject)) &&
      (!needle ||
        [account.name, account.vatNumber, account.taxCode, account.foreignId, account.partitario]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(needle)),
  );
  // The lines of an open sub-ledger stay in their order: the balance runs down them.
  const sorting = useTableSort(`ledgers.${cycle}`, rows, {
    name: (account) => account.name,
    opening: (account) => amountSort(ledgerBalance(cycle, account.opening)),
    debit: (account) => amountSort(account.debit),
    credit: (account) => amountSort(account.credit),
    closing: (account) => amountSort(ledgerBalance(cycle, account.closing)),
    subject: (account) => account.subject?.name ?? null,
  });
  const total = (key: 'opening' | 'debit' | 'credit' | 'closing') =>
    fromCents(ofCycle.reduce((sum, account) => sum + toCents(account[key]), 0));
  const differences = ofCycle.filter((account) => account.previousDifference !== null);
  const unmatched = ofCycle.filter((account) => account.partitario && !account.subject).length;

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <h1>Mastrini</h1>
          <p className="muted">
            I mastrini clienti e fornitori del commercialista, anno per anno: si consultano e non si
            modificano; un anno si sostituisce caricandolo di nuovo.
            {ledger.data &&
              ` Anno ${ledger.data.year} caricato da «${ledger.data.fileName}» il ${formatTimestamp(ledger.data.importedAt)}.`}
          </p>
        </div>
      </div>

      {years.data && years.data.length === 0 && (
        <p className="notice info">
          Nessun mastrino caricato
          {canWrite ? ': caricalo con «Carica il mastrino dell’anno».' : '.'}
        </p>
      )}
      {years.data && years.data.length > 0 && (
        <div className="ledger-toolbar">
          <div className="segmented-small" role="group" aria-label="Anno">
            {[...years.data]
              .sort((a, b) => a.year - b.year)
              .map((item) => (
                <button
                  key={item.year}
                  type="button"
                  className={item.year === shown ? 'selected' : ''}
                  onClick={() => {
                    setYear(item.year);
                    setOpen(new Set());
                  }}
                >
                  {item.year}
                </button>
              ))}
          </div>
        </div>
      )}

      {ledger.data && (
        <nav className="subtabs" aria-label="Vista">
          <button
            type="button"
            className={view === 'soggetti' ? 'subtab selected' : 'subtab'}
            onClick={() => setView('soggetti')}
          >
            Soggetti
          </button>
          <button
            type="button"
            className={view === 'abbinamenti' ? 'subtab selected' : 'subtab'}
            onClick={() => setView('abbinamenti')}
          >
            Abbinamenti pagamenti ↔ fatture
          </button>
          <button
            type="button"
            className={view === 'quadratura' ? 'subtab selected' : 'subtab'}
            onClick={() => setView('quadratura')}
          >
            Quadratura al 31/12
          </button>
        </nav>
      )}
      {ledger.data && view === 'abbinamenti' && <LedgerMatchesView year={ledger.data.year} />}
      {ledger.data && view === 'quadratura' && (
        <LedgerSquaringView
          year={ledger.data.year}
          onOpenMatches={(name) => {
            setMatchesQuery(name);
            setView('abbinamenti');
          }}
        />
      )}
      {ledger.data && view === 'soggetti' && (
        <>
          <nav className="subtabs" aria-label="Ciclo">
            {LEDGER_CYCLES.map((key) => (
              <button
                key={key}
                type="button"
                className={cycle === key ? 'subtab selected' : 'subtab'}
                onClick={() => setCycle(key)}
              >
                {LEDGER_CYCLE_LABELS[key]}
              </button>
            ))}
          </nav>
          <div className="treasury-tiles">
            <Tile label="Saldo iniziale" value={ledgerBalance(cycle, total('opening'))} />
            <Tile
              label={cycle === 'attivo' ? 'Fatturato (dare)' : 'Pagato (dare)'}
              value={total('debit')}
            />
            <Tile
              label={cycle === 'attivo' ? 'Incassato (avere)' : 'Fatturato (avere)'}
              value={total('credit')}
            />
            <Tile
              label={`Saldo al 31/12/${ledger.data.year}`}
              value={ledgerBalance(cycle, total('closing'))}
            />
          </div>
          <p className="muted small">
            Saldi nel verso del ciclo:{' '}
            {cycle === 'attivo' ? 'credito verso il cliente' : 'debito verso il fornitore'}; un
            importo negativo è un acconto o un credito al contrario.
          </p>
          {differences.length > 0 && (
            <p className="notice small">
              {differences.length === 1 ? '1 soggetto ha' : `${differences.length} soggetti hanno`}{' '}
              un saldo iniziale diverso dal saldo finale del {ledger.data.year - 1}: sono segnati
              nella colonna del saldo iniziale.
            </p>
          )}
          {!ledger.data.previousLoaded && (
            <p className="muted small">
              Il {ledger.data.year - 1} non è caricato: i saldi iniziali non si possono controllare.
            </p>
          )}

          <div className="ledger-toolbar">
            <label className="search-box">
              <Icon name="search" />
              <input
                value={query}
                placeholder="Soggetto, partita IVA o codice fiscale"
                onChange={(event) => setQuery(event.target.value)}
              />
            </label>
            <select
              className="compact-select"
              aria-label="Saldo"
              value={onlyOpen ? 'open' : 'all'}
              onChange={(event) => setOnlyOpen(event.target.value === 'open')}
            >
              <option value="open">Solo con saldo</option>
              <option value="all">Tutti i soggetti</option>
            </select>
            <select
              className="compact-select"
              aria-label="Abbinamento"
              value={match}
              onChange={(event) => setMatch(event.target.value as MatchFilter)}
            >
              <option value="all">Abbinamento: tutti</option>
              <option value="matched">Abbinati all’Anagrafica</option>
              <option value="unmatched">Da abbinare ({unmatched})</option>
            </select>
          </div>

          <div className="table-wrap">
            <table className="data-table ledger-table">
              <thead>
                <tr>
                  <SortHeader sort={sorting} column="name">
                    Soggetto
                  </SortHeader>
                  <SortHeader sort={sorting} column="opening" numeric>
                    Saldo iniziale
                  </SortHeader>
                  <SortHeader sort={sorting} column="debit" numeric>
                    Dare
                  </SortHeader>
                  <SortHeader sort={sorting} column="credit" numeric>
                    Avere
                  </SortHeader>
                  <SortHeader sort={sorting} column="closing" numeric>
                    Saldo finale
                  </SortHeader>
                  <SortHeader sort={sorting} column="subject">
                    Anagrafica
                  </SortHeader>
                </tr>
              </thead>
              <tbody>
                {sorting.rows.map((account) => {
                  const expanded = open.has(account.id);
                  return (
                    <Fragment key={account.id}>
                      <tr
                        className="openable"
                        onClick={(event) => {
                          if ((event.target as HTMLElement).closest('button, a')) return;
                          const next = new Set(open);
                          if (expanded) next.delete(account.id);
                          else next.add(account.id);
                          setOpen(next);
                        }}
                      >
                        <td>
                          <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>{' '}
                          <strong>{account.name}</strong>
                          <small className="muted block">
                            {[
                              account.vatNumber ?? account.taxCode ?? account.foreignId,
                              account.partitario
                                ? `partitario ${account.partitario} · conto ${account.accountCode}`
                                : `conto ${account.accountCode} ${account.accountName}`,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </small>
                        </td>
                        <td className="numeric">
                          {formatMoney(ledgerBalance(cycle, account.opening))}
                          {account.previousDifference && (
                            <small className="block warn-text">
                              diverso dal {ledger.data!.year - 1} di{' '}
                              {formatMoney(ledgerBalance(cycle, account.previousDifference))}
                            </small>
                          )}
                        </td>
                        <td className="numeric">{formatMoney(account.debit)}</td>
                        <td className="numeric">{formatMoney(account.credit)}</td>
                        <td className="numeric">
                          <strong>{formatMoney(ledgerBalance(cycle, account.closing))}</strong>
                        </td>
                        <td>
                          {account.subject ? (
                            <>
                              <Link to={`/anagrafica/${account.subject.id}`}>
                                {account.subject.name}
                              </Link>
                              {account.subject.match === 'manual' && (
                                <small className="muted block">abbinato a mano</small>
                              )}
                            </>
                          ) : account.partitario ? (
                            <span className="pill warn">Da abbinare</span>
                          ) : (
                            <span className="muted">—</span>
                          )}
                          {canWrite && account.partitario && (
                            <button
                              type="button"
                              className="link small block"
                              onClick={() => setLinking(account)}
                            >
                              {account.subject ? 'Cambia' : 'Abbina'}
                            </button>
                          )}
                          {canWrite && canCreate && account.partitario && !account.subject && (
                            <Link
                              className="small block"
                              to={newSubjectFromLedger(account, '/tesoreria/mastrini')}
                            >
                              Crea in Anagrafica
                            </Link>
                          )}
                        </td>
                      </tr>
                      {expanded && <EntryRows account={account} />}
                    </Fragment>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="muted">
                      Nessun soggetto con questi filtri.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      {ledger.isError && <p className="error">{ledger.error.message}</p>}
      {loading && (
        <LoadDialog
          onClose={() => setLoading(false)}
          onLoaded={(loaded) => {
            setLoading(false);
            setYear(loaded);
          }}
        />
      )}
      {linking && <LinkDialog account={linking} onClose={() => setLinking(null)} />}
    </section>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="treasury-tile">
      <span>{label}</span>
      <strong>{formatMoney(value)}</strong>
    </div>
  );
}

/** The lines of a sub-ledger, read when it is opened. */
function EntryRows({ account }: { account: LedgerAccountResponse }) {
  const entries = useQuery({
    queryKey: keys.entries(account.id),
    queryFn: () =>
      apiRequest<LedgerEntryResponse[]>(`/treasury/ledgers/accounts/${account.id}/entries`),
  });
  if (!entries.data) {
    return (
      <tr className="ledger-entry">
        <td colSpan={6} className="muted">
          {entries.isError ? entries.error.message : 'Caricamento…'}
        </td>
      </tr>
    );
  }
  if (entries.data.length === 0) {
    return (
      <tr className="ledger-entry">
        <td colSpan={6} className="muted">
          Nessuna riga nel mastrino.
        </td>
      </tr>
    );
  }
  return (
    <>
      {entries.data.map((entry) => (
        <tr key={entry.id} className="ledger-entry">
          <td>
            {entry.registrationDate ? formatDate(entry.registrationDate) : ''} ·{' '}
            {entry.opening ? 'Saldo iniziale' : entry.causal}
            {entry.documentNumber && (
              <>
                {' '}
                · doc. {entry.documentNumber}
                {entry.documentDate ? ` del ${formatDate(entry.documentDate)}` : ''}
              </>
            )}
            {entry.description && entry.description !== entry.causal && (
              <small className="muted block">{entry.description}</small>
            )}
          </td>
          <td />
          <td className="numeric">{Number(entry.debit) ? formatMoney(entry.debit) : ''}</td>
          <td className="numeric">{Number(entry.credit) ? formatMoney(entry.credit) : ''}</td>
          <td className="numeric">{formatMoney(ledgerBalance(account.cycle, entry.balance))}</td>
          <td />
        </tr>
      ))}
    </>
  );
}

/** «Carica il mastrino dell'anno»: the Excel checked and counted, then loaded. */
function LoadDialog({
  onClose,
  onLoaded,
}: {
  onClose: () => void;
  onLoaded: (year: number) => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [file, setFile] = useState<{ base64: string; name: string } | null>(null);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const send = (chosen: { base64: string; name: string }, dryRun: boolean) =>
    apiRequest<LedgerImportResult>('/treasury/ledgers/import', {
      method: 'POST',
      body: { file: chosen.base64, fileName: chosen.name, dryRun },
    });
  const preview = useMutation({
    mutationFn: (chosen: { base64: string; name: string }) => send(chosen, true),
  });
  const load = useMutation({
    mutationFn: () => send(file!, false),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: keys.years });
      onLoaded(result.year);
    },
  });
  const choose = async (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0];
    if (!chosen) return;
    const bytes = new Uint8Array(await chosen.arrayBuffer());
    let binary = '';
    for (let index = 0; index < bytes.length; index += 8192) {
      binary += String.fromCharCode(...bytes.subarray(index, index + 8192));
    }
    const read = { base64: btoa(binary), name: chosen.name };
    setFile(read);
    preview.mutate(read);
  };
  const result = preview.data;
  return (
    <dialog ref={dialogRef} className="dialog narrow" onClose={onClose}>
      <div className="stack">
        <h2>Carica il mastrino dell’anno</h2>
        <p className="muted small">
          Il file Excel con i fogli «Soggetti» e «Movimenti», un anno per file. Prima si controlla,
          poi si carica: un anno già caricato viene sostituito per intero.
        </p>
        <input type="file" accept=".xlsx" onChange={(event) => void choose(event)} />
        {preview.isPending && <p className="muted">Controllo del file…</p>}
        {preview.error && <p className="error">{preview.error.message}</p>}
        {result && (
          <dl className="totals">
            <div>
              <dt>Anno</dt>
              <dd>{result.year}</dd>
            </div>
            {LEDGER_CYCLES.map((key) => (
              <div key={key}>
                <dt>{LEDGER_CYCLE_LABELS[key]}</dt>
                <dd>
                  {result.accounts[key]} soggetti · saldo{' '}
                  {formatMoney(ledgerBalance(key, result.totals[key].closing))}
                </dd>
              </div>
            ))}
            <div>
              <dt>Righe</dt>
              <dd>{result.entries}</dd>
            </div>
            <div>
              <dt>Abbinati all’Anagrafica</dt>
              <dd>{result.matched}</dd>
            </div>
            <div>
              <dt>Da abbinare</dt>
              <dd>{result.unmatched}</dd>
            </div>
            <div>
              <dt>Controllo dei saldi</dt>
              <dd>
                <span className="pill ok">Torna</span>
              </dd>
            </div>
          </dl>
        )}
        {result?.replaces && (
          <p className="notice small">Sostituisce il mastrino {result.year} già caricato.</p>
        )}
        {load.error && <p className="error">{load.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={load.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={!result || load.isPending}
            onClick={() => load.mutate()}
          >
            {load.isPending ? 'Attendere…' : 'Carica'}
          </button>
        </div>
      </div>
    </dialog>
  );
}

/**
 * «Crea in Anagrafica» (1.47): the new subject page filled with what the
 * ledger knows (name, identifiers, customer or supplier); once saved it is
 * matched to the ledger and the page comes back here.
 */
export function newSubjectFromLedger(
  account: Pick<
    LedgerAccountResponse,
    'cycle' | 'name' | 'vatNumber' | 'taxCode' | 'foreignId' | 'matchKey'
  >,
  back: string,
): string {
  const params = new URLSearchParams({
    name: account.name,
    ruolo: account.cycle === 'attivo' ? 'C' : 'F',
    mastrino: account.matchKey,
    ritorno: back,
  });
  const foreign = /^([A-Z]{2})(.+)$/.exec(account.foreignId?.toUpperCase() ?? '');
  if (account.vatNumber) params.set('piva', account.vatNumber.replace(/^IT(?=\d{11}$)/i, ''));
  else if (foreign) {
    params.set('paese', foreign[1]!);
    params.set('piva', foreign[2]!);
  }
  if (account.taxCode) params.set('cf', account.taxCode);
  return `/anagrafica/nuovo?${params.toString()}`;
}

/** Matching a subject of the ledgers to one of Anagrafica: once, for every year. */
export function LinkDialog({
  account,
  onClose,
}: {
  account: Pick<
    LedgerAccountResponse,
    'matchKey' | 'name' | 'vatNumber' | 'taxCode' | 'foreignId' | 'subject'
  >;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const subjects = useSubjectSearch(true);
  const [query, setQuery] = useState('');
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const save = useMutation({
    mutationFn: (subjectId: string | null) =>
      apiRequest('/treasury/ledgers/links', {
        method: 'PUT',
        body: { key: account.matchKey, subjectId },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.years });
      onClose();
    },
  });
  const needle = query.trim().toLowerCase();
  const found =
    needle.length >= 2
      ? (subjects.data ?? []).filter((subject) => subject.search.includes(needle)).slice(0, 8)
      : [];
  return (
    <dialog ref={dialogRef} className="dialog narrow" onClose={onClose}>
      <div className="stack">
        <h2>Abbina all’Anagrafica</h2>
        <p>
          <strong>{account.name}</strong>
          <small className="muted block">
            {account.vatNumber ??
              account.taxCode ??
              account.foreignId ??
              'senza partita IVA né codice fiscale'}
          </small>
        </p>
        <p className="muted small">
          L’abbinamento vale per tutti gli anni dei mastrini e resta nel registro attività.
        </p>
        <label className="search-box">
          <Icon name="search" />
          <input
            value={query}
            autoFocus
            placeholder="Denominazione o partita IVA del soggetto"
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        {found.length > 0 && (
          <ul className="search-results">
            {found.map((subject) => (
              <li key={subject.id}>
                <button
                  type="button"
                  onClick={() => save.mutate(subject.id)}
                  disabled={save.isPending}
                >
                  <strong>{subject.name}</strong>
                  <small className="muted">
                    {[subject.vatNumber ?? subject.taxCode, subject.city]
                      .filter(Boolean)
                      .join(' · ')}
                  </small>
                </button>
              </li>
            ))}
          </ul>
        )}
        {save.error && <p className="error">{save.error.message}</p>}
        <div className="actions">
          {account.subject?.match === 'manual' && (
            <button type="button" onClick={() => save.mutate(null)} disabled={save.isPending}>
              Togli l’abbinamento
            </button>
          )}
          <button type="button" onClick={onClose}>
            Chiudi
          </button>
        </div>
      </div>
    </dialog>
  );
}
